import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  Flex,
  Progress,
  Spinner,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import Logo from '@/components/Logo';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectIsOpen,
  selectForceShow,
  selectMode,
  selectProgress,
  selectMessages,
  setProgress,
} from '@/store/slices/loadingOverlaySlice';

import { AnimatePresence, motion } from 'framer-motion';

const ROTATE_INTERVAL = 900; // Faster message rotation

// Animation for logo (similar to EmptyContent)
const logoAnimation = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  transition: {
    duration: 0.5,
    delay: 0.2, // Delay for logo
  },
};

export const LoadingOverlayModal: React.FC = () => {
  // Split complex selectors to prevent new object creation
  const dispatch = useAppDispatch();
  const storeIsOpen = useAppSelector(selectIsOpen);
  const forceShow = useAppSelector(selectForceShow);
  const mode = useAppSelector(selectMode);
  const progress = useAppSelector(selectProgress);
  const messages = useAppSelector(selectMessages);

  const handleSetProgress = useCallback(
    (value: number) => {
      dispatch(setProgress(value));
    },
    [dispatch]
  );

  // Memoize derived values
  const isOpen = useMemo(
    () => storeIsOpen || forceShow,
    [storeIsOpen, forceShow]
  );
  const [messageIdx, setMessageIdx] = useState(0);
  const [shouldShow, setShouldShow] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Controls modal visibility with minimum display time
  useEffect(() => {
    if (isOpen) {
      setShouldShow(true);
      setMessageIdx(0); // Reset messageIdx only on open
      setShowProgressBar(true);
      handleSetProgress(0); // Reset progress only on open
      setIsClosing(false);
    } else {
      // On close, set progress to 100%
      setIsClosing(true);
      handleSetProgress(100);
      // Delay before closing for smoothness
      const timeout = setTimeout(() => {
        setShouldShow(false);
        setShowProgressBar(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [handleSetProgress, isOpen]);

  // Update progress on message change (only if not closing)
  useEffect(() => {
    if (!shouldShow || messages.length === 0 || !isOpen || isClosing) return;
    const newProgress = ((messageIdx + 1) / messages.length) * 100;
    handleSetProgress(newProgress);
  }, [handleSetProgress, messageIdx, messages.length, shouldShow, isOpen, isClosing]);

  // Message rotation: only forward, not looping (only if not closing)
  useEffect(() => {
    if (!shouldShow || messages.length < 2 || isClosing) return;
    if (messageIdx >= messages.length - 1) return; // Stop at last message

    const interval = setInterval(() => {
      if (!shouldShow || isClosing) return; // Safety: do not update if modal is hidden or closing
      setMessageIdx(idx => (idx < messages.length - 1 ? idx + 1 : idx));
    }, ROTATE_INTERVAL);

    return () => clearInterval(interval);
  }, [shouldShow, messages.length, messageIdx, isClosing]);

  // Reset index on mode change
  useEffect(() => {
    setMessageIdx(0);
  }, [mode]);

  // Block scroll when modal is open
  useEffect(() => {
    if (shouldShow) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [shouldShow]);

  return (
    shouldShow && (
      <Dialog.Root open>
        <Dialog.Content
          fullscreen
          size="large"
          className="loading-overlay-modal"
        >
          <VisuallyHidden>
            <Dialog.Title>Loading...</Dialog.Title>
          </VisuallyHidden>
          <AnimatePresence>
            <Flex
              direction="column"
              align="center"
              gap="4"
              style={{ width: '100%' }}
            >
              {/* Animated logo */}
              <motion.div
                style={{ fontSize: '0.8rem', marginBottom: 32 }}
                initial={logoAnimation.initial}
                animate={logoAnimation.animate}
                transition={logoAnimation.transition}
              >
                <Logo />
              </motion.div>

              {/* Progress Bar */}
              <div
                style={{
                  width: '100%',
                  maxWidth: 300,
                  marginBottom: 16,
                }}
              >
                {showProgressBar && (
                  <Progress
                    value={progress}
                    max={100}
                    size="medium"
                    highContrast
                    color="primary"
                    variant="surface"
                    aria-label="Loading progress"
                  />
                )}
              </div>

              <Text
                size="xsmall"
                mb="4"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  transition: 'opacity 0.3s',
                }}
              >
                {messages[messageIdx]} <Spinner size="3" className="spinner" />
              </Text>
            </Flex>
          </AnimatePresence>
        </Dialog.Content>
      </Dialog.Root>
    )
  );
};
